import Cookies from 'js-cookie';
import {
  CLIENT_TYPES,
  LOGIN_REQUIRED_CLIENTS,
} from '../../consts/authentication';
import { getGithubClientId } from '../../consts/git';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { OIDC_PROVIDER } from '../../network/httpClients/OIDCAuthenticationClient/consts';
import { MARKETING_DEFAULTS, QUERY_PARAMS } from '../../routes/consts';
import { getAppUrl } from '../../utils/dynamicSettings';
import { isAirgappedMode } from '../../utils/env';
import { getThisUrl } from '../../utils/routes';
import {
  buildURLWithQueryParams,
  getQueryParam,
  getOIDCAuthorizeEndpointUrl,
  getTraceIdFromSessionStorage,
  getSessionIdFromSessionStorage,
  buildSearchParams,
  getMarketingParamsFromLocalStorage,
} from '../../utils/url';
import {
  getGoogleClientId,
  V2_REDIRECT_URI_FOR_EXTERNAL_CLIENTS,
} from './consts';

/**
 * Get the init client query param
 * @returns The init client query param, or the default 'portal' if not set
 */
export const getInitClientQueryParam = () => {
  return getQueryParam(QUERY_PARAMS.INIT_CLIENT) || 'portal';
};

/**
 * Get the redirect URI for the V2 authentication
 * @returns The redirect URI for the V2 authentication
 */
export const getV2RedirectURI = () => {
  const initClientQueryParam = getInitClientQueryParam();

  if (LOGIN_REQUIRED_CLIENTS.includes(initClientQueryParam)) {
    return buildURLWithQueryParams(
      V2_REDIRECT_URI_FOR_EXTERNAL_CLIENTS,
      buildSearchParams({
        [QUERY_PARAMS.AUTH_V2]: 'true',
        [QUERY_PARAMS.INIT_CLIENT]: initClientQueryParam,
        [QUERY_PARAMS.SESSION_ID]: getSessionIdFromSessionStorage(),
        [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
      }),
    );
  }

  return buildURLWithQueryParams(getAppUrl(), {
    auth_v2: 'true',
  });
};

/**
 * Get the URL to the Google login page
 * @returns The URL to the Google login page
 */
export const getGoogleLoginUrl = () => {
  const redirectURI = getV2RedirectURI();
  const urlAddress = getOIDCAuthorizeEndpointUrl();

  const initClientQueryParam = getInitClientQueryParam();
  const SCOPE = 'email profile openid';
  const RESPONSE_TYPE = 'code';
  const ACCESS_TYPE = 'offline';
  const STATE = btoa(`${getThisUrl()};portal`);
  const url = new URL(urlAddress);
  const sessionId = getSessionIdFromSessionStorage();
  const traceId = getTraceIdFromSessionStorage();
  const originalMethod = getQueryParam(QUERY_PARAMS.METHOD);
  const originalReturnUrl = getQueryParam(QUERY_PARAMS.RETURN_URL);
  const enterpriseUserName = getQueryParam(QUERY_PARAMS.ENTERPRISE_USER_NAME);

  const searchParams = buildSearchParams({
    [QUERY_PARAMS.CLIENT_ID]: getGoogleClientId(),
    [QUERY_PARAMS.SCOPE]: SCOPE,
    [QUERY_PARAMS.RESPONSE_TYPE]: RESPONSE_TYPE,
    [QUERY_PARAMS.ACCESS_TYPE]: ACCESS_TYPE,
    [QUERY_PARAMS.STATE]: STATE,
    [QUERY_PARAMS.PROVIDER]: OIDC_PROVIDER.GOOGLE,
    [QUERY_PARAMS.REDIRECT_URI]: redirectURI,
    [QUERY_PARAMS.SESSION_ID]: sessionId,
    [QUERY_PARAMS.TRACE_ID]: traceId,
    [QUERY_PARAMS.INIT_CLIENT]: initClientQueryParam,
    [QUERY_PARAMS.ENTERPRISE_USER_NAME]: enterpriseUserName,
    [QUERY_PARAMS.RETURN_URL]: originalReturnUrl,
    [QUERY_PARAMS.METHOD]: originalMethod,
  });

  url.search = searchParams.toString();

  return url.toString();
};

/**
 * Get the URL to the Github login page
 * @returns The URL to the Github login page
 */
export const getGithubLoginUrl = () => {
  const STATE = btoa(`${getThisUrl()}`);
  const SCOPE = 'read:user,user:email';

  const redirectURI = getV2RedirectURI();
  const urlAddress = getOIDCAuthorizeEndpointUrl();

  const url = new URL(urlAddress);
  const initClientQueryParam = getInitClientQueryParam();
  const sessionId = getSessionIdFromSessionStorage();
  const traceId = getTraceIdFromSessionStorage();
  const originalMethod = getQueryParam(QUERY_PARAMS.METHOD);
  const originalReturnUrl = getQueryParam(QUERY_PARAMS.RETURN_URL);
  const enterpriseUserName = getQueryParam(QUERY_PARAMS.ENTERPRISE_USER_NAME);

  const searchParams = buildSearchParams({
    [QUERY_PARAMS.CLIENT_ID]: getGithubClientId(),
    [QUERY_PARAMS.REDIRECT_URI]: redirectURI,
    [QUERY_PARAMS.SCOPE]: SCOPE,
    [QUERY_PARAMS.STATE]: STATE,
    [QUERY_PARAMS.PROVIDER]: OIDC_PROVIDER.GITHUB,
    [QUERY_PARAMS.SESSION_ID]: sessionId,
    [QUERY_PARAMS.TRACE_ID]: traceId,
    [QUERY_PARAMS.INIT_CLIENT]: initClientQueryParam,
    [QUERY_PARAMS.ENTERPRISE_USER_NAME]: enterpriseUserName,
    [QUERY_PARAMS.METHOD]: originalMethod,
    [QUERY_PARAMS.RETURN_URL]: originalReturnUrl,
  });

  url.search = searchParams.toString();

  return url.toString();
};

/**
 * Get the local storage key for the send again email
 * @param email The email of the user
 * @returns The local storage key for the send again email
 */
export const createSendAgainLocalStorageKey = (email: string | undefined) =>
  `${LOCAL_STORAGE_KEYS.SEND_AGAIN_CLICK_COUNT}_${email || 'unknown'}`;

/**
 * Get the session ID - from URL in airgapped mode, from session storage otherwise
 * @returns The session ID
 */
export const getSessionIdQueryParam = () => {
  return getQueryParam(QUERY_PARAMS.SESSION_ID);
};

/**
 * Get the trace ID - from URL in airgapped mode, from session storage otherwise
 * @returns The trace ID
 */
export const getTraceIdQueryParam = () => {
  return getQueryParam(QUERY_PARAMS.TRACE_ID);
};

/**
 * Get base authentication parameters for SSO login
 * @param email The user's email
 * @returns Object with login hint, session ID, and trace ID
 */
export const getSSOBaseParams = (email: string) => ({
  [QUERY_PARAMS.LOGIN_HINT]: email,
  [QUERY_PARAMS.SESSION_ID]: getSessionIdFromSessionStorage(),
  [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
});

/**
 * Get provider-specific parameters for SSO login
 * In airgapped mode: uses Zitadel provider with client_id and gets session/trace from URL
 * In normal mode: uses WorkOS provider with session/trace from session storage
 * @param email The user's email
 * @returns URLSearchParams with provider-specific parameters
 */
export const getSSOProviderParams = (email: string): URLSearchParams => {
  const isAirgapped = isAirgappedMode();

  if (isAirgapped) {
    const params = new URLSearchParams({
      [QUERY_PARAMS.LOGIN_HINT]: email,
      [QUERY_PARAMS.PROVIDER]: OIDC_PROVIDER.OIDC,
      [QUERY_PARAMS.CLIENT_ID]: CLIENT_TYPES.PORTAL,
      [QUERY_PARAMS.INIT_CLIENT]: getInitClientQueryParam(),
    });

    const sessionId = getSessionIdQueryParam();
    const traceId = getTraceIdQueryParam();

    if (sessionId) {
      params.set(QUERY_PARAMS.SESSION_ID, sessionId);
    }

    if (traceId) {
      params.set(QUERY_PARAMS.TRACE_ID, traceId);
    }

    return params;
  }

  const params = new URLSearchParams({
    [QUERY_PARAMS.LOGIN_HINT]: email,
    [QUERY_PARAMS.PROVIDER]: OIDC_PROVIDER.WORKOS,
  });

  const sessionId = getSessionIdFromSessionStorage();
  const traceId = getTraceIdFromSessionStorage();

  if (sessionId) {
    params.set(QUERY_PARAMS.SESSION_ID, sessionId);
  }

  if (traceId) {
    params.set(QUERY_PARAMS.TRACE_ID, traceId);
  }

  return params;
};

/**
 * Get optional SSO parameters (like enterprise user name)
 * @returns Object with optional parameters (string values only)
 */
export const getSSOOptionalParams = (): Record<string, string> => {
  const params: Record<string, string> = {};
  const enterpriseUserName = getQueryParam(QUERY_PARAMS.ENTERPRISE_USER_NAME);

  if (enterpriseUserName) {
    params[QUERY_PARAMS.ENTERPRISE_USER_NAME] = enterpriseUserName;
  }

  return params;
};

/**
 * Get authentication tracking parameters (session_id, trace_id, init_client)
 * In airgapped mode: gets from URL parameters
 * In normal mode: gets session_id and trace_id from session storage
 * @returns URLSearchParams with authentication tracking parameters
 */
export const getAuthTrackingParams = (): URLSearchParams => {
  const deviceId = Cookies.get('hubspotutk');
  const { productSource, productType } = getMarketingParamsFromLocalStorage();

  if (isAirgappedMode()) {
    const traceId = getTraceIdQueryParam();
    const sessionId = getSessionIdQueryParam();
    const initClient = getInitClientQueryParam();

    return buildSearchParams({
      [QUERY_PARAMS.TRACE_ID]: traceId,
      [QUERY_PARAMS.SESSION_ID]: sessionId,
      [QUERY_PARAMS.INIT_CLIENT]: initClient,
    });
  } else {
    const traceId = getTraceIdFromSessionStorage();
    const sessionId = getSessionIdFromSessionStorage();

    return buildSearchParams({
      [QUERY_PARAMS.TRACE_ID]: traceId,
      [QUERY_PARAMS.SESSION_ID]: sessionId,
      [QUERY_PARAMS.PRODUCT_SOURCE]:
        productSource ?? MARKETING_DEFAULTS.PRODUCT_SOURCE,
      [QUERY_PARAMS.PRODUCT_TYPE]:
        productType ?? MARKETING_DEFAULTS.PRODUCT_TYPE,
      [QUERY_PARAMS.DEVICE_ID]: deviceId,
    });
  }
};

/**
 * Format a rating value for display
 * Converts large numbers (>= 1000) to K notation (e.g., 1.5K, 2K)
 * @param value The rating value to format (number or string)
 * @returns Formatted rating string
 */
export const formatRating = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numValue >= 1000) {
    const thousands = numValue / 1000;

    return `${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}K`;
  }

  return numValue.toString();
};
